import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  connectAuthPost, getConnectToken, getConnectUser, clearConnectSession,
  getConnectDestination, setConnectDestination,
  normalizePerson, normalizeMeetup, statusLabel, formatWhen
} from './connectApi';
import NotificationsBell from './NotificationsBell';
import ConnectSelect from './ConnectSelect';
import ConnectNav from './ConnectNav';
import CreateMeetup from './CreateMeetup';
import MeetupDetail from './MeetupDetail';
import './styles/ConnectMeet.css';

const WHEN_OPTIONS = [
  { id: '', label: 'Anytime' },
  { id: 'now', label: 'Now' },
  { id: 'today', label: 'Today' },
  { id: 'tonight', label: 'Tonight' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'week', label: 'This week' },
];

const QUICK_ACTIVITIES = ['Coffee', 'Drinks', 'Beach', 'Walk', 'Just chat'];

/**
 * MEET — what + who + when.
 *
 * Shows people who are open to meeting alongside meetups you can join, both
 * scoped to one destination. Meetups stay visible while a destination is
 * still building, since a standing plan is how a small community starts.
 */
export default function ConnectMeet() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [destinationId, setDestinationId] = useState(getConnectDestination());
  const [destination, setDestination] = useState(null);

  const [tags, setTags] = useState([]);
  const [meetups, setMeetups] = useState([]);
  const [people, setPeople] = useState([]);
  const [peopleReason, setPeopleReason] = useState('');
  const [when, setWhen] = useState('');
  const [activityTagId, setActivityTagId] = useState('');

  const [presence, setPresence] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openMeetupId, setOpenMeetupId] = useState(null);
  const [notice, setNotice] = useState('');
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [pendingFeedback, setPendingFeedback] = useState([]);

  const activityTags = tags.filter((t) => t.type === 'activity');

  /* ---------------------------------------------------------------- load -- */

  useEffect(() => {
    if (!getConnectToken()) {
      navigate('/connect');
      return;
    }
    setUser(getConnectUser());

    const boot = async () => {
      try {
        const [destRes, tagRes] = await Promise.all([
          connectAuthPost('connect/destinations/list'),
          connectAuthPost('connect/tags/list'),
        ]);

        if (destRes.success) {
          const list = destRes.data.destinations;
          setDestinations(list);

          // Fall back to the member's own destination, then the first open one.
          const stored = getConnectDestination();
          const me = getConnectUser();
          const chosen =
            (stored && list.some((d) => d.id === stored) && stored) ||
            (me?.destinationId && list.some((d) => d.id === me.destinationId) && me.destinationId) ||
            (list[0] && list[0].id) || null;

          setDestinationId(chosen);
          if (chosen) setConnectDestination(chosen);
        }
        if (tagRes.success) setTags(tagRes.data.tags);
      } catch (err) {
        setNotice('Could not reach the server.');
      }
    };

    boot();
  }, [navigate]);

  const loadForDestination = useCallback(async () => {
    if (!destinationId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const payload = { destination_id: destinationId };
      if (when) payload.when = when;
      if (activityTagId) payload.activity_tag_id = parseInt(activityTagId, 10);

      const peoplePayload = { destination_id: destinationId, open_to_meet_only: true };
      if (activityTagId) peoplePayload.tag_ids = [parseInt(activityTagId, 10)];

      const [meetRes, peopleRes, presenceRes, feedbackRes] = await Promise.all([
        connectAuthPost('connect/meetups/list', payload),
        connectAuthPost('connect/people/discover', peoplePayload),
        connectAuthPost('connect/presence/get', { destination_id: destinationId }),
        connectAuthPost('connect/meetups/pending-feedback'),
      ]);

      if (feedbackRes.success) setPendingFeedback(feedbackRes.data.pending);

      if (meetRes.success) {
        setMeetups(meetRes.data.meetups.map(normalizeMeetup));
        setDestination(meetRes.data.destination);
      } else {
        setMeetups([]);
      }

      if (peopleRes.success) {
        setPeople(peopleRes.data.people.map(normalizePerson));
        setPeopleReason(peopleRes.data.reason || '');
        if (peopleRes.data.destination) setDestination(peopleRes.data.destination);
      }

      if (presenceRes.success) setPresence(presenceRes.data);
    } catch (err) {
      setNotice('Could not load this destination.');
    } finally {
      setIsLoading(false);
    }
  }, [destinationId, when, activityTagId]);

  useEffect(() => { loadForDestination(); }, [loadForDestination]);

  /* -------------------------------------------------------------- actions -- */

  const handleDestinationChange = (id) => {
    const parsed = parseInt(id, 10) || null;
    setDestinationId(parsed);
    setConnectDestination(parsed);
    setWhen('');
    setActivityTagId('');
  };

  const handleFreeNow = async (minutes, activityName) => {
    const tag = activityTags.find((t) => t.name === activityName);
    try {
      const result = await connectAuthPost('connect/availability/start', {
        destination_id: destinationId,
        minutes,
        activity_ids: tag ? [tag.id] : [],
      });
      setNotice(result.response || '');
      loadForDestination();
    } catch (err) {
      setNotice('Could not update your availability.');
    }
  };

  const handleEndAvailability = async () => {
    await connectAuthPost('connect/availability/end');
    setNotice('');
    loadForDestination();
  };

  // Private, and never shown to anyone — it exists to measure whether
  // meetups actually happen and whether they felt safe.
  const submitFeedback = async (meetupId, happened, feltSafe) => {
    try {
      await connectAuthPost('connect/meetups/feedback', {
        meetup_id: meetupId,
        happened,
        felt_safe: feltSafe,
      });
    } catch (err) {
      // Not worth interrupting anyone over; it is asked again next load.
    }
    setPendingFeedback((prev) => prev.filter((f) => f.meetup_id !== meetupId));
  };

  const handleLogout = async () => {
    try { await connectAuthPost('connect/auth/logout'); } catch (err) { /* clear locally regardless */ }
    clearConnectSession();
    navigate('/connect');
  };

  if (!user) return null;

  const isBuilding = destination && destination.state === 'building';
  const freeNow = presence && presence.availability;

  return (
    <div className="meet-page">
      <header className="connect-profile-header">
        <Link to="/" className="connect-profile-logo">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="OutCaribbean" />
        </Link>
        <ConnectNav active="meet" />
        <div className="connect-header-actions">
          <NotificationsBell />
          <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
          <button className="connect-mobile-nav-toggle" onClick={() => setShowMobileNav(true)}>
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      {showMobileNav && (
        <>
          <div className="connect-mobile-nav-overlay active" onClick={() => setShowMobileNav(false)}></div>
          <div className="connect-mobile-nav-menu active">
            <div className="connect-mobile-nav-header">
              <h3>Menu</h3>
              <button className="connect-mobile-nav-close" onClick={() => setShowMobileNav(false)}>×</button>
            </div>
            <div className="connect-mobile-nav-links">
              <ConnectNav active="meet" variant="mobile" onNavigate={() => setShowMobileNav(false)} />
            </div>
            <div className="connect-mobile-nav-footer">
              <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
            </div>
          </div>
        </>
      )}

      <main className="meet-main">
        {/* Where */}
        <div className="meet-place">
          <h1 className="meet-place-title">
            <label className="meet-label" htmlFor="meet-destination">Meet people in</label>
            <ConnectSelect
              id="meet-destination"
              variant="title"
              ariaLabel="Destination"
              value={destinationId || ''}
              placeholder={destinations.length === 0 ? 'No destinations open yet' : 'Choose a destination'}
              options={destinations.map((d) => ({ value: d.id, label: d.name }))}
              onChange={handleDestinationChange}
            />
          </h1>
          {destination && (
            <span className={`meet-state meet-state-${destination.state}`}>
              {destination.state === 'building' && 'Building community'}
              {destination.state === 'growing' && `Growing · ${destination.people_range} here`}
              {destination.state === 'active' && `Active · ${destination.people_range} here`}
            </span>
          )}
        </div>

        {notice && <div className="meet-notice">{notice}</div>}

        {pendingFeedback.map((f) => (
          <div key={f.meetup_id} className="meet-feedback">
            <div>
              <strong>How was {f.title}?</strong>
              <span>Only we see this. It is never shown to anyone who was there.</span>
            </div>
            <div className="meet-feedback-actions">
              <button className="meet-btn ghost small"
                      onClick={() => submitFeedback(f.meetup_id, true, true)}>
                Went well
              </button>
              <button className="meet-btn ghost small"
                      onClick={() => submitFeedback(f.meetup_id, true, false)}>
                Happened, felt off
              </button>
              <button className="meet-btn ghost small"
                      onClick={() => submitFeedback(f.meetup_id, false, false)}>
                Did not happen
              </button>
            </div>
          </div>
        ))}

        {/* Quick Meet */}
        <section className={`meet-quick ${freeNow ? 'is-live' : ''}`}>
          {freeNow ? (
            <div className="meet-quick-active">
              <div>
                <strong>You are shown as free right now</strong>
                <span>{freeNow.minutes_left} minutes left</span>
              </div>
              <button className="meet-btn ghost" onClick={handleEndAvailability}>End</button>
            </div>
          ) : (
            <>
              <h2>I am free right now</h2>
              <p>Tell people you are around for the next couple of hours.</p>
              <div className="meet-quick-chips">
                {QUICK_ACTIVITIES.map((a) => (
                  <button key={a} className="meet-chip" onClick={() => handleFreeNow(120, a)}>{a}</button>
                ))}
              </div>
            </>
          )}
        </section>

        {/* What and when */}
        <div className="meet-toolbar">
          <div className="meet-toolbar-row" role="group" aria-label="When">
            {WHEN_OPTIONS.map((o) => (
              <button
                key={o.id}
                type="button"
                aria-pressed={when === o.id}
                className={`meet-chip ${when === o.id ? 'active' : ''}`}
                onClick={() => setWhen(o.id)}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div className="meet-toolbar-row">
            <ConnectSelect
              ariaLabel="Filter by activity"
              value={activityTagId}
              placeholder="Any activity"
              options={[{ value: '', label: 'Any activity' }]
                .concat(activityTags.map((t) => ({ value: t.id, label: t.name })))}
              onChange={setActivityTagId}
            />
            <span className="meet-toolbar-spacer">
              <button
                className="meet-btn primary"
                onClick={() => setShowCreate(true)}
                disabled={!destinationId}
              >
                Create meetup
              </button>
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="meet-empty"><h3>Loading…</h3></div>
        ) : (
          <>
            {/* Meetups */}
            <section className="meet-section">
              <h2>Meetups {meetups.length > 0 && <span className="meet-count">{meetups.length}</span>}</h2>
              {meetups.length === 0 ? (
                <div className="meet-empty">
                  <h3>Nothing planned yet</h3>
                  <p>Create the first one — it is how a destination gets going.</p>
                </div>
              ) : (
                <div className="meet-grid">
                  {meetups.map((m) => (
                    <button key={m.id} className="meet-card" onClick={() => setOpenMeetupId(m.id)}>
                      <div className="meet-card-top">
                        {m.activity && <span className="meet-card-activity">{m.activity}</span>}
                        {m.alcoholFree && <span className="meet-card-flag">Alcohol-free</span>}
                      </div>
                      <h3>{m.title}</h3>
                      <p className="meet-card-when">{formatWhen(m.startAt)}</p>
                      {m.area && <p className="meet-card-area">Around {m.area}</p>}
                      <div className="meet-card-foot">
                        <span className="meet-card-host">
                          {m.host.full_name} · {statusLabel(m.host.status)}
                        </span>
                        <span className={`meet-card-spaces ${m.isFull ? 'full' : ''}`}>
                          {m.isFull ? 'Full' : `${m.spacesLeft} space${m.spacesLeft === 1 ? '' : 's'} left`}
                        </span>
                      </div>
                      {m.myStatus === 'pending' && <span className="meet-card-mine">Request sent</span>}
                      {m.myStatus === 'approved' && !m.isHost && <span className="meet-card-mine">You are going</span>}
                      {m.isHost && <span className="meet-card-mine">You are hosting</span>}
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* People open to meeting */}
            <section className="meet-section">
              <h2>Open to meeting</h2>
              {isBuilding ? (
                <div className="meet-empty">
                  <h3>Building community here</h3>
                  <p>
                    We do not show people until a destination has enough members for
                    that to be safe. Meetups above still work.
                  </p>
                </div>
              ) : peopleReason === 'too_specific' ? (
                <div className="meet-empty">
                  <h3>Too few people match</h3>
                  <p>Broaden your filters — narrow searches are hidden to protect people.</p>
                </div>
              ) : people.length === 0 ? (
                <div className="meet-empty">
                  <h3>Nobody is marked open right now</h3>
                  <p>Try again later, or create a meetup people can join.</p>
                </div>
              ) : (
                <div className="meet-people">
                  {people.map((p) => (
                    <div key={p.id} className="meet-person">
                      <div className="meet-person-avatar">
                        {p.profilePicture
                          ? <img src={p.profilePicture} alt={p.fullName} />
                          : <span>{p.fullName.charAt(0).toUpperCase()}</span>}
                      </div>
                      <div className="meet-person-body">
                        <div className="meet-person-head">
                          <strong>{p.fullName}</strong>
                          {p.pronouns && <span className="meet-person-pronouns">{p.pronouns}</span>}
                          <span className="meet-person-status">{statusLabel(p.status)}</span>
                        </div>
                        {p.trip && <p className="meet-person-trip">{p.trip.until_label}</p>}
                        {p.availability && (
                          <p className="meet-person-free">Free for {p.availability.minutes_left} more minutes</p>
                        )}
                        {p.reason && <p className="meet-person-reason">{p.reason}</p>}
                      </div>
                      <Link to={`/connect/user/${p.id}`} className="meet-btn ghost small">View</Link>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {showCreate && (
        <CreateMeetup
          destinationId={destinationId}
          activityTags={activityTags}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadForDestination(); }}
        />
      )}

      {openMeetupId && (
        <MeetupDetail
          meetupId={openMeetupId}
          onClose={() => setOpenMeetupId(null)}
          onChanged={loadForDestination}
        />
      )}
    </div>
  );
}
